import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { useUiStore } from '../../state/ui-store.js'

/** The whole story map, headers included — not the scroll viewport. */
const MAP_SELECTOR = '[data-testid="board-map"]'

/**
 * Canvas dimensions are capped by the browser (Safari is the strictest, around 16k px a
 * side). Scaling down past this keeps a very large board exporting rather than failing.
 */
const MAX_CANVAS_EDGE = 12_000

export function safeFileName(name: string): string {
  const cleaned = name.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
  return cleaned.length > 0 ? cleaned : 'board'
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Two frames: one for React to commit the unwindowed cards, one for layout to settle. */
function nextFrames(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
}

/**
 * Renders the board to a canvas with every card mounted and the scroll clip released,
 * then always restores the board — a thrown capture must not leave the UI in export mode.
 */
async function captureBoard(): Promise<HTMLCanvasElement | null> {
  const map = document.querySelector<HTMLElement>(MAP_SELECTOR)
  if (!map) return null

  const { setExporting } = useUiStore.getState()
  setExporting(true)
  document.documentElement.dataset.exporting = 'true'
  try {
    await nextFrames()
    const width = Math.max(map.scrollWidth, map.offsetWidth)
    const height = Math.max(map.scrollHeight, map.offsetHeight)
    const scale = Math.min(2, MAX_CANVAS_EDGE / Math.max(width, height, 1))
    return await html2canvas(map, {
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      scale: Math.max(0.3, scale),
      backgroundColor: getComputedStyle(document.body).backgroundColor,
      useCORS: true,
      logging: false,
    })
  } finally {
    delete document.documentElement.dataset.exporting
    setExporting(false)
  }
}

export async function exportBoardPng(boardName: string): Promise<void> {
  const canvas = await captureBoard()
  if (!canvas) return
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (blob) download(blob, `${safeFileName(boardName)}.png`)
}

export async function exportBoardPdf(boardName: string): Promise<void> {
  const canvas = await captureBoard()
  if (!canvas) return
  // One page sized to the board, so nothing is cropped and nothing is split mid-card.
  const orientation = canvas.width >= canvas.height ? 'landscape' : 'portrait'
  const pdf = new jsPDF({ orientation, unit: 'px', format: [canvas.width, canvas.height] })
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height)
  pdf.save(`${safeFileName(boardName)}.pdf`)
}
